/**
 * @module features/devices/api/devices-api
 * @description Typed, schema-validated fetchers for Super Admin Device Management
 * (Constitution XIII). Hooks live in the sibling use*.ts files.
 */
import { z } from "zod";
import { apiClient, ApiSuccessEnvelope, AppError } from "@/lib/api-client";
import {
  BulkImportReport,
  BulkImportReportSchema,
  DeviceCreateFormValues,
  DeviceItem,
  DeviceItemSchema,
  DeviceList,
  DeviceListSchema,
  DeviceStats,
  DeviceStatsSchema,
  DeviceTypeDescriptor,
  DeviceTypeListSchema,
  DeviceUpdateInput,
  DevicesQueryParams,
} from "../types";

const BASE = "/super-admin/devices";

export const deviceKeys = {
  all: ["devices"] as const,
  list: (params: DevicesQueryParams) => ["devices", "list", params] as const,
  detail: (id: string) => ["devices", "detail", id] as const,
  stats: ["devices", "stats"] as const,
  types: ["devices", "types"] as const,
};

/** Validate an envelope payload; surface contract drift as a typed AppError. */
function parseOrThrow<T>(schema: z.ZodType<T>, data: unknown, requestId?: string): T {
  const result = schema.safeParse(data);
  if (!result.success) {
    throw new AppError(
      {
        error_code: "RESPONSE_VALIDATION_ERROR",
        error_message: "The server returned an unexpected response.",
        error_details:
          "The response did not match the expected format. Please try again or contact support.",
        request_id: requestId,
        httpStatus: 502,
      },
      502
    );
  }
  return result.data;
}

export async function getDevices(params: DevicesQueryParams = {}): Promise<DeviceList> {
  const qp = new URLSearchParams();
  if (params.page) qp.set("page", String(params.page));
  if (params.page_size) qp.set("page_size", String(params.page_size));
  if (params.search) qp.set("search", params.search);
  if (params.device_type && params.device_type !== "all") qp.set("device_type", params.device_type);
  if (params.status && params.status !== "all") qp.set("status", params.status);
  const res = await apiClient.get<ApiSuccessEnvelope<unknown>>(`${BASE}/?${qp.toString()}`);
  return parseOrThrow(DeviceListSchema, res.data.data, res.data.request_id);
}

export async function getDevice(id: string): Promise<DeviceItem> {
  const res = await apiClient.get<ApiSuccessEnvelope<unknown>>(`${BASE}/${id}/`);
  return parseOrThrow(DeviceItemSchema, res.data.data, res.data.request_id);
}

export async function createDevice(values: DeviceCreateFormValues): Promise<DeviceItem> {
  const payload = {
    ...values,
    registered_device_id: values.registered_device_id?.trim() || undefined,
  };
  const res = await apiClient.post<ApiSuccessEnvelope<unknown>>(`${BASE}/`, payload);
  return parseOrThrow(DeviceItemSchema, res.data.data, res.data.request_id);
}

export async function updateDevice(id: string, input: DeviceUpdateInput): Promise<DeviceItem> {
  const res = await apiClient.put<ApiSuccessEnvelope<unknown>>(`${BASE}/${id}/`, input);
  return parseOrThrow(DeviceItemSchema, res.data.data, res.data.request_id);
}

export async function decommissionDevice(id: string): Promise<void> {
  await apiClient.delete(`${BASE}/${id}/`);
}

export async function reactivateDevice(
  id: string,
  status: "PROVISIONED" | "ACTIVE" = "PROVISIONED"
): Promise<DeviceItem> {
  const res = await apiClient.post<ApiSuccessEnvelope<unknown>>(`${BASE}/${id}/reactivate/`, {
    status,
  });
  return parseOrThrow(DeviceItemSchema, res.data.data, res.data.request_id);
}

export async function getDeviceStats(): Promise<DeviceStats> {
  const res = await apiClient.get<ApiSuccessEnvelope<unknown>>(`${BASE}/stats/`);
  return parseOrThrow(DeviceStatsSchema, res.data.data, res.data.request_id);
}

export async function getDeviceTypes(): Promise<DeviceTypeDescriptor[]> {
  const res = await apiClient.get<ApiSuccessEnvelope<unknown>>(`${BASE}/types/`);
  return parseOrThrow(DeviceTypeListSchema, res.data.data, res.data.request_id);
}

export async function uploadBulkCsv(deviceType: string, file: File): Promise<BulkImportReport> {
  const form = new FormData();
  form.append("device_type", deviceType);
  form.append("file", file);
  const res = await apiClient.post<ApiSuccessEnvelope<unknown>>(`${BASE}/bulk-import/`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return parseOrThrow(BulkImportReportSchema, res.data.data, res.data.request_id);
}

/** Fetch the CSV template and trigger a browser download via a revocable blob URL. */
export async function downloadCsvTemplate(deviceType: string): Promise<void> {
  const res = await apiClient.get<Blob>(
    `${BASE}/bulk-import/template/?device_type=${encodeURIComponent(deviceType)}`,
    { responseType: "blob" }
  );
  const url = URL.createObjectURL(res.data);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = `${deviceType}_import_template.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
